// Ported from dev-scrips-pm-react src/documents/components/lab-row-editor.tsx (PROD-1119 documents module).
import { useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  AnchoredPopover,
  AnchoredPopoverContent,
  AnchoredPopoverTrigger,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@scripsteam/scrips-signal-ds';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { useLabOrderCodes } from '@/documents/services/use-lab-order-codes';
import {
  unitOfMeasurementToUnitType,
  useUnitOfMeasurements,
  type UnitOfMeasurementRow,
} from '@/documents/services/use-unit-of-measurements';
import type {
  LabData,
  PatientDocumentLab,
  UnitType,
} from '@/documents/types/document';
// PROD-2743 design sheet §3 (icons): DS names ON only (?ef=frameV2); OFF keeps today's lucide icon, unchanged.

/**
 * LabRowEditor — single editable lab row inside the Labs sub-form
 * (PROD-797 PR 3).
 *
 * Each row mirrors Flutter `PatientDocumentLab`:
 *   - `labName` (LabData)  ← picked from `useLabOrderCodes`
 *   - `result` (free text)
 *   - `unitType` (UnitType) ← picked from `useUnitOfMeasurements`
 *   - `isDeleted` (soft-delete flag; managed by parent's remove button)
 *
 * Wire-shape contract reminder (per CONVERSION_NOTES_PROD-797.md):
 *   - `labName` ships the lab-master's inner `code` object as-is
 *     (already lowercased: `{ code, display, system }`).
 *   - `unitType` MUST be lowercased — the master endpoint returns
 *     PascalCase (`Code` / `Display`) but the document body uses
 *     camelCase. `unitOfMeasurementToUnitType()` handles the translation.
 *
 * Combobox UX: type-to-search with 250ms debounce, click to select,
 * keyboard nav via shadcn's `Command` primitive (built on cmdk).
 * Selected value renders as a button; clicking re-opens the search.
 */

interface LabRowEditorProps {
  row: PatientDocumentLab;
  index: number;
  onChange: (next: PatientDocumentLab) => void;
  onRemove: () => void;
  /**
   * Visual variant.
   *
   *   "default" — bordered card wrapper, 4-column layout
   *               (lab | result | unit | X remove). Used by the
   *               Documents tab's free-form Lab document upload.
   *
   *   "compact" — no card wrapper, no remove button, 3-column
   *               layout with light-tinted inputs. Matches the
   *               Flutter Lab Linkage upload screen — rows are
   *               flat list items, deletion happens by clearing
   *               the lab field (empty rows are dropped at save).
   */
  variant?: 'default' | 'compact';
}

export function LabRowEditor({
  row,
  index,
  onChange,
  onRemove,
  variant = 'default',
}: LabRowEditorProps) {
  const compact = variant === 'compact';
  return (
    <div
      className={cn(
        'grid gap-2 items-start',
        compact
          ? 'grid-cols-lab-row'
          : 'grid-cols-lab-row-edit rounded-md border border-border-default bg-surface-card px-3 py-2.5',
      )}
      aria-label={`Lab row ${index + 1}`}
    >
      <LabNamePicker
        value={row.labName ?? null}
        onChange={(labName) => onChange({ ...row, labName })}
        compact={compact}
      />
      <input
        type="text"
        inputMode="decimal"
        value={row.result ?? ''}
        onChange={(e) => onChange({ ...row, result: e.target.value })}
        placeholder="Result"
        className={cn(
          'h-10 px-3 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary',
          compact
            ? 'border border-border-default bg-grey-100'
            : 'border border-border-default bg-surface-card',
        )}
      />
      <UnitPicker
        value={row.unitType ?? null}
        onChange={(unitType) => onChange({ ...row, unitType })}
        compact={compact}
      />
      {compact ? null : (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove lab row ${index + 1}`}
          className="icon-action self-center -mx-1"
        >
          <X className="size-4" aria-hidden />
        </button>
      )}
    </div>
  );
}

// ─── Lab name combobox ──────────────────────────────────────────────────

interface LabNamePickerProps {
  value: LabData | null;
  onChange: (next: LabData | null) => void;
  compact?: boolean;
}

export function LabNamePicker({ value, onChange, compact = false }: LabNamePickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  // Debounce + enabled gating (Samer review PR #139 §4):
  //   - `useDebounce` is trailing-edge — `debounced` only updates after
  //     250ms of inactivity on `search`.
  //   - `useLabOrderCodes` internally gates on `enabled && trimmed > 0`.
  //     So an empty `search` (or just-cleared field) → empty debounced
  //     → hook sees `enabled: false` → query short-circuits.
  //   - Any in-flight request from a prior non-empty search is aborted
  //     by React Query via the AbortSignal forwarded through the hook's
  //     queryFn — no stale results race fresh ones.
  const debounced = useDebounce(search, 250);
  const query = useLabOrderCodes({ searchQuery: debounced, enabled: open });

  const label = value?.display ?? value?.code ?? 'Pick a lab…';

  return (
    <AnchoredPopover open={open} onOpenChange={setOpen}>
      <AnchoredPopoverTrigger
        render={
          <button
            type="button"
            aria-label="Lab name"
            className={cn(
              'flex h-10 w-full items-center justify-between rounded-md border border-border-default px-3 text-left text-sm',
              compact ? 'bg-grey-100' : 'bg-surface-card',
              !value && 'text-text-hint',
            )}
          />
        }
      >
        <span className="truncate">{label}</span>
        <ChevronDown className="size-4 text-text-secondary shrink-0 ml-2" aria-hidden />
      </AnchoredPopoverTrigger>
      <AnchoredPopoverContent collisionAvoidance={{ fallbackAxisSide: 'end' }} className="w-80 p-0 gap-3" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search labs…"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {debounced.trim().length === 0 ? (
              <CommandEmpty>Type to search the lab catalogue.</CommandEmpty>
            ) : query.isLoading ? (
              <CommandEmpty>Searching…</CommandEmpty>
            ) : query.isError ? (
              <CommandEmpty>Couldn't load labs.</CommandEmpty>
            ) : (query.data ?? []).length === 0 ? (
              <CommandEmpty>No matches.</CommandEmpty>
            ) : (
              <CommandGroup>
                {(query.data ?? []).map((row) => {
                  const code = row.code?.code ?? '';
                  const display = row.code?.display ?? '';
                  const isSelected = value?.code === code;
                  return (
                    <CommandItem
                      key={row.Id}
                      value={`${code} ${display}`}
                      onSelect={() => {
                        onChange({
                          code,
                          display,
                        });
                        setOpen(false);
                        setSearch('');
                      }}
                      data-checked={isSelected}
                    >
                      <span className="flex-1 truncate">
                        <span className="font-medium">{display}</span>
                        <span className="ml-2 text-xs text-text-muted">{code}</span>
                      </span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </AnchoredPopoverContent>
    </AnchoredPopover>
  );
}

// ─── Unit-of-measurement combobox ──────────────────────────────────────

interface UnitPickerProps {
  value: UnitType | null;
  onChange: (next: UnitType | null) => void;
  compact?: boolean;
}

export function UnitPicker({ value, onChange, compact = false }: UnitPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  // Same trailing-edge debounce + enabled-gating pattern as
  // LabNamePicker above — see that comment block for the contract.
  const debounced = useDebounce(search, 250);
  const query = useUnitOfMeasurements({ query: debounced, enabled: open });

  const label = value?.display ?? value?.code ?? 'Pick a unit…';

  return (
    <AnchoredPopover open={open} onOpenChange={setOpen}>
      <AnchoredPopoverTrigger
        render={
          <button
            type="button"
            aria-label="Unit of measurement"
            className={cn(
              'flex h-10 w-full items-center justify-between rounded-md border border-border-default px-3 text-left text-sm',
              compact ? 'bg-grey-100' : 'bg-surface-card',
              !value && 'text-text-hint',
            )}
          />
        }
      >
        <span className="truncate">{label}</span>
        <ChevronDown className="size-4 text-text-secondary shrink-0 ml-2" aria-hidden />
      </AnchoredPopoverTrigger>
      <AnchoredPopoverContent collisionAvoidance={{ fallbackAxisSide: 'end' }} className="w-80 p-0 gap-3" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search units…"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {debounced.trim().length === 0 ? (
              <CommandEmpty>Type to search units of measurement.</CommandEmpty>
            ) : query.isLoading ? (
              <CommandEmpty>Searching…</CommandEmpty>
            ) : query.isError ? (
              <CommandEmpty>Couldn't load units.</CommandEmpty>
            ) : (query.data ?? []).length === 0 ? (
              <CommandEmpty>No matches.</CommandEmpty>
            ) : (
              <CommandGroup>
                {(query.data ?? []).map((row: UnitOfMeasurementRow) => {
                  const isSelected = value?.code === row.Code;
                  return (
                    <CommandItem
                      key={row.Id}
                      value={`${row.Code} ${row.Display}`}
                      onSelect={() => {
                        // Translate PascalCase master shape →
                        // camelCase document-body shape. The wire
                        // gotcha is documented in
                        // `use-unit-of-measurements.ts`.
                        onChange(unitOfMeasurementToUnitType(row));
                        setOpen(false);
                        setSearch('');
                      }}
                      data-checked={isSelected}
                    >
                      <span className="flex-1 truncate">
                        <span className="font-medium">{row.Display}</span>
                        <span className="ml-2 text-xs text-text-muted">
                          {row.Code}
                        </span>
                      </span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </AnchoredPopoverContent>
    </AnchoredPopover>
  );
}
